import React, { ChangeEvent, useEffect, useState } from 'react';
import { Autocomplete, List, ListItemButton, TextField } from '@mui/material';
import { Control, Controller } from 'react-hook-form';
import { styled, useTheme } from '@mui/material/styles';
import validator from '../../../../lib/validator';

type AutocompleteOption = {
  label: string;
  value: any;
  optionKey?: string;
  disabled?: boolean;
};

const LONG_LABEL_THRESHOLD = 70;

const CustomTextField = styled(TextField)(() => ({
  '& .MuiInputBase-input': {
    fontSize: '1em',
  },
  '& .MuiInputBase-root': {
    fontSize: '1em',
    height: '38px',
  },
  '& .MuiInputLabel-root': {
    fontSize: '1em',
  },
}));

const getWrapLabelSx = (theme: import('@mui/material/styles').Theme) => ({
  '&:focus-within .MuiInputLabel-root.MuiInputLabel-shrink': {
    top: 'auto',
    bottom: '100%',
    transform: 'translate(14px, 8px) scale(0.75)',
    transformOrigin: 'top left',
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'unset',
    maxWidth: 'calc(133% - 24px)',
    lineHeight: 1.25,
    backgroundColor: theme.palette.background.paper,
    padding: '0 4px',
    zIndex: 1,
  },
  '&:focus-within .MuiOutlinedInput-notchedOutline legend': {
    display: 'none',
  },
  '&:focus-within': {
    marginTop: theme.spacing(5),
  },
});

const CustomListboxComponent = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLElement>
>((listboxProps, ref) => {
  return (
    <List
      {...listboxProps}
      ref={ref}
      sx={{
        width: '100%',
        maxWidth: '100%',
        bgcolor: 'background.paper',
        position: 'relative',
        overflow: 'auto',
        maxHeight: '300px',
        '& ul': { padding: 0 },
      }}>
      {listboxProps.children}
    </List>
  );
});
CustomListboxComponent.displayName = 'CustomListboxComponent';

export const InputAutocompleteAdapter = (props: {
  name: string;
  options?: AutocompleteOption[];
  control: Control<any>;
  keyLabel?: string;
  keyValue?: string;
  schema: validator.AnySchema;
  error?: any;
  label?: string;
  disabled?: boolean;
  onBlur?: any;
  borderBottom?: boolean;
  customOnChange?: (e?: ChangeEvent<HTMLInputElement>) => void;
  onSearchInputChange?: (value: string) => void;
  searchNormalizer?: (value: string) => string;
  liveSearch?: boolean;
  searchPageSize?: number;
  sx?: any;
}) => {
  const {
    name,
    options,
    control,
    label,
    schema,
    customOnChange,
    onSearchInputChange,
    searchNormalizer,
    liveSearch = false,
    searchPageSize = 20,
    disabled,
    sx,
    onBlur,
    borderBottom = true,
    ...rest
  } = props;
  const theme = useTheme();
  const [inputOptions, setInputOptions] = useState<AutocompleteOption[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [visibleOptionsCount, setVisibleOptionsCount] = useState(searchPageSize);
  const isLongLabel = (label?.length || 0) > LONG_LABEL_THRESHOLD;

  const buildNestedValue = (path: string, value: unknown) =>
    path
      .split('.')
      .reduceRight<unknown>((nestedValue, pathPart) => ({ [pathPart]: nestedValue }), value);

  const validateField = async (value: unknown) => {
    if (!schema) return true;

    try {
      if (schema.type === 'object') {
        await schema.validateAt(name, buildNestedValue(name, value));
      } else {
        await schema.validate(value);
      }

      return true;
    } catch (error) {
      return error instanceof Error ? error.message : 'Campo inválido';
    }
  };

  const filterOptionsBySearch = React.useCallback(
    (value: string): AutocompleteOption[] => {
      const normalizedSearchValue = searchNormalizer
        ? searchNormalizer(value)
        : value.toLowerCase();

      return (options || []).filter((option) => {
        const optionLabel = option?.label?.toString() || '';
        const normalizedOptionLabel = searchNormalizer
          ? searchNormalizer(optionLabel)
          : optionLabel.toLowerCase();

        return normalizedOptionLabel.includes(normalizedSearchValue);
      });
    },
    [options, searchNormalizer]
  );

  const matchedOptions = React.useMemo<AutocompleteOption[]>(() => {
    if (!liveSearch) return inputOptions;

    return filterOptionsBySearch(searchValue);
  }, [filterOptionsBySearch, inputOptions, liveSearch, searchValue]);

  const optionsToDisplay = React.useMemo<AutocompleteOption[]>(
    () => (liveSearch ? matchedOptions.slice(0, visibleOptionsCount) : matchedOptions),
    [liveSearch, matchedOptions, visibleOptionsCount]
  );

  useEffect(() => {
    if (!liveSearch) {
      setInputOptions(options || []);
    }
  }, [liveSearch, options]);

  const handleListboxScroll = (event: React.UIEvent<HTMLUListElement>) => {
    if (!liveSearch || visibleOptionsCount >= matchedOptions.length) return;

    const listbox = event.currentTarget;
    const isNearBottom = listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 50;

    if (isNearBottom) {
      setVisibleOptionsCount((currentCount) => currentCount + searchPageSize);
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{ validate: validateField }}
      render={({ field, fieldState }) => (
        <Autocomplete<AutocompleteOption, false, false, false>
          {...field}
          {...rest}
          sx={{ ...sx }}
          size="small"
          disabled={disabled}
          blurOnSelect
          onOpen={() => {
            if (liveSearch) {
              setSearchValue('');
              setVisibleOptionsCount(searchPageSize);
              return;
            }

            setInputOptions(options || []);
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            return option?.value === value?.value;
          }}
          value={
            (field?.value !== undefined &&
              field?.value !== null &&
              options?.find((x) => x?.value === field?.value)) ||
            null
          }
          options={optionsToDisplay}
          getOptionDisabled={(option) => Boolean(option.disabled)}
          getOptionKey={(option) => option.optionKey || String(option.value)}
          filterOptions={
            liveSearch ? (availableOptions: AutocompleteOption[]) => availableOptions : undefined
          }
          getOptionLabel={(option) => option.label}
          ListboxComponent={CustomListboxComponent}
          ListboxProps={liveSearch ? ({ onScroll: handleListboxScroll } as any) : undefined}
          renderOption={(prop: any, option) => {
            return <ListItemButton {...prop}>{`${option?.label}`}</ListItemButton>;
          }}
          onInputChange={(_e, value) => {
            if (liveSearch) {
              setSearchValue(value);
              setVisibleOptionsCount(searchPageSize);
              onSearchInputChange?.(value);
              return;
            }

            setInputOptions(filterOptionsBySearch(value));
            onSearchInputChange?.(value);
          }}
          onChange={(_, value: { label: string; value: any } | any) => {
            field.onChange(value?.value);
            if (customOnChange)
              customOnChange({
                target: { name, value: value?.value },
              } as any);
          }}
          onBlur={onBlur || field.onBlur}
          renderInput={(params) => (
            <CustomTextField
              {...params}
              variant="outlined"
              label={label || 'Selecciona una opción'}
              error={Boolean(fieldState.error?.message)}
              helperText={`${fieldState.error?.message || ''}`}
              sx={{
                mb: theme.spacing(1),
                fontSize: '1em',
                ...(isLongLabel ? getWrapLabelSx(theme) : {}),
                ...(!borderBottom ? { 'margin-bottom': '0px' } : {}),
              }}
            />
          )}
        />
      )}
    />
  );
};
